import type { KeyboardEvent } from "react";

import { Check, Star } from "lucide-react";

import { cn } from "../../lib/utils";

export type Tier =
  | "self-reported"
  | "document-backed"
  | "bank-verified"
  | "lender-approved";

export type TierBadgeSize = "sm" | "md" | "lg";
export type TierBadgeVariant = "on-light" | "on-ink";
export type FreshnessState = "fresh" | "aging" | "stale";

export interface TierBadgeProps {
  tier: Tier;
  size?: TierBadgeSize;
  variant?: TierBadgeVariant;
  verifiedAt?: Date;
  freshnessState?: FreshnessState;
  showFreshnessOnSmall?: boolean;
  onExplainRequest?: () => void;
  className?: string;
  "data-testid"?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const FRESH_CUTOFF_DAYS = 60;
const STALE_CUTOFF_DAYS = 90;

const TIER_LABEL: Record<Tier, string> = {
  "self-reported": "Self-reported",
  "document-backed": "Document",
  "bank-verified": "Verified",
  "lender-approved": "Pre-approved",
};

const SIZE_CLASSES: Record<TierBadgeSize, string> = {
  sm: "min-h-6 px-2 py-0.5 text-[11px] tracking-[0.1em] gap-1",
  md: "min-h-8 px-3 py-1 text-xs tracking-[0.12em] gap-1.5",
  lg: "min-h-10 px-4 py-1.5 text-sm tracking-[0.14em] gap-2",
};

const SIZE_ICON: Record<TierBadgeSize, string> = {
  sm: "size-3",
  md: "size-3.5",
  lg: "size-4",
};

type TierIcon = typeof Check | typeof Star | null;

const TIER_ICON: Record<Tier, TierIcon> = {
  "self-reported": null,
  "document-backed": null,
  "bank-verified": Check,
  "lender-approved": Star,
};

function deriveFreshness(verifiedAt: Date | undefined): FreshnessState {
  if (!verifiedAt) return "fresh";
  const ageDays = (Date.now() - verifiedAt.getTime()) / DAY_MS;
  if (ageDays < FRESH_CUTOFF_DAYS) return "fresh";
  if (ageDays < STALE_CUTOFF_DAYS) return "aging";
  return "stale";
}

function relativeVerified(verifiedAt: Date): string {
  const ageDays = Math.max(
    0,
    Math.floor((Date.now() - verifiedAt.getTime()) / DAY_MS),
  );
  if (ageDays <= 1) return "verified today";
  if (ageDays < 14) return `verified ${ageDays} days ago`;
  const weeks = Math.floor(ageDays / 7);
  if (weeks < 9) return `verified ${weeks} weeks ago`;
  const months = Math.floor(ageDays / 30);
  return `verified ${months} months ago`;
}

function tierTone(
  tier: Tier,
  variant: TierBadgeVariant,
  freshness: FreshnessState,
): string {
  if (variant === "on-ink") {
    switch (tier) {
      case "self-reported":
        return "bg-white/10 text-panel-ink-foreground/90";
      case "document-backed":
        if (freshness === "stale")
          return "bg-white/10 text-panel-ink-foreground/70";
        if (freshness === "aging")
          return "bg-warning/30 text-panel-ink-foreground ring-1 ring-warning/50";
        return "bg-info/30 text-panel-ink-foreground ring-1 ring-info/50";
      case "bank-verified":
        return "bg-primary/55 text-panel-ink-foreground ring-1 ring-primary/70";
      case "lender-approved":
        return "bg-panel-ink-foreground/20 text-panel-ink-foreground ring-1 ring-panel-ink-foreground/40";
    }
  }
  switch (tier) {
    case "self-reported":
      return "bg-muted text-muted-foreground";
    case "document-backed":
      if (freshness === "stale") return "bg-muted text-muted-foreground";
      if (freshness === "aging")
        return "bg-warning/18 text-warning-foreground";
      return "bg-info/15 text-info";
    case "bank-verified":
      return "bg-primary/10 text-primary";
    case "lender-approved":
      return "bg-panel-ink text-panel-ink-foreground";
  }
}

function freshnessSuffix(
  freshness: FreshnessState,
  verifiedAt: Date | undefined,
): string | null {
  if (freshness === "stale") return "verification expired";
  if (verifiedAt) return relativeVerified(verifiedAt);
  return null;
}

export function TierBadge({
  tier,
  size = "md",
  variant = "on-light",
  verifiedAt,
  freshnessState,
  showFreshnessOnSmall = false,
  onExplainRequest,
  className,
  "data-testid": dataTestId,
}: TierBadgeProps) {
  const freshness = freshnessState ?? deriveFreshness(verifiedAt);
  const showFreshness =
    tier === "document-backed" && (size !== "sm" || showFreshnessOnSmall);
  const suffix = showFreshness ? freshnessSuffix(freshness, verifiedAt) : null;

  const label = TIER_LABEL[tier];
  const ariaLabel = suffix ? `${label} · ${suffix}` : label;

  const baseClass = cn(
    "inline-flex items-center rounded-full font-semibold uppercase",
    "transition-colors",
    SIZE_CLASSES[size],
    tierTone(tier, variant, freshness),
    onExplainRequest &&
      "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    className,
  );

  const Icon = TIER_ICON[tier];

  const content = (
    <>
      {Icon ? (
        <Icon
          aria-hidden="true"
          className={cn(SIZE_ICON[size], "shrink-0")}
          fill={tier === "lender-approved" ? "currentColor" : "none"}
          strokeWidth={2.5}
        />
      ) : null}
      <span>{label}</span>
      {suffix ? (
        <span
          aria-hidden="true"
          className="font-medium normal-case tracking-normal opacity-80"
          data-freshness={freshness}
        >
          · {suffix}
        </span>
      ) : null}
    </>
  );

  if (onExplainRequest) {
    const handleKey = (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onExplainRequest();
      }
    };
    return (
      <button
        aria-label={ariaLabel}
        className={baseClass}
        data-freshness={showFreshness ? freshness : undefined}
        data-tier={tier}
        data-testid={dataTestId}
        onClick={onExplainRequest}
        onKeyDown={handleKey}
        type="button"
      >
        {content}
      </button>
    );
  }

  return (
    <span
      aria-label={ariaLabel}
      className={baseClass}
      data-freshness={showFreshness ? freshness : undefined}
      data-tier={tier}
      data-testid={dataTestId}
    >
      {content}
    </span>
  );
}
